import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Briefcase, User as UserIcon, Building2, Sparkles, ArrowRight } from "lucide-react";
import UnfitBadge from "@/components/UnfitBadge";

/**
 * Phase 62 — Persistent top-nav global search.
 *
 * A short-height input that always sits in the header. On focus (or Cmd+K)
 * it expands into an inline dropdown with three columns: Candidates, Jobs,
 * Clients. Candidate rows show AI score + summary so recruiters can decide
 * without opening the profile.
 *
 * Cmd+K also opens the existing CommandMenu palette (in parallel) — the
 * input is complementary: fast, mouse-driven, always visible.
 */
export default function GlobalSearch() {
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [results, setResults] = useState({ candidates: [], jobs: [], clients: [] });
    const [loading, setLoading] = useState(false);
    const [hi, setHi] = useState(0);
    const inputRef = useRef(null);
    const wrapRef = useRef(null);
    const debounceRef = useRef(null);
    const navigate = useNavigate();

    // Global "/" hotkey (like GitHub/Slack) focuses the search.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "/" && !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) {
                e.preventDefault();
                inputRef.current?.focus();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // Debounced fetch
    useEffect(() => {
        if (!q.trim()) {
            setResults({ candidates: [], jobs: [], clients: [] });
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setLoading(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const { data } = await api.get("/search/global", { params: { q, limit: 5 } });
                setResults(data || { candidates: [], jobs: [], clients: [] });
                setHi(0);
            } catch {
                // fail silent
            } finally { setLoading(false); }
        }, 180);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [q]);

    // Close on outside click
    useEffect(() => {
        const onClick = (e) => {
            if (!wrapRef.current?.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    // Flat list for keyboard nav
    const flat = useMemo(() => [
        ...(results.candidates || []).map((c) => ({ kind: "candidate", item: c })),
        ...(results.jobs || []).map((j) => ({ kind: "job", item: j })),
        ...(results.clients || []).map((cl) => ({ kind: "client", item: cl })),
    ], [results]);

    const go = (item) => {
        setOpen(false);
        setQ("");
        if (item.kind === "candidate") return navigate(`/candidates/${item.item.id}`);
        if (item.kind === "job") return navigate(`/jobs/${item.item.id}`);
        if (item.kind === "client") return navigate(`/clients`);
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, flat.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
        else if (e.key === "Enter" && flat[hi]) { e.preventDefault(); go(flat[hi]); }
        else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
    };

    const hasResults = flat.length > 0;

    return (
        <div ref={wrapRef} className="relative w-full max-w-xl" data-testid="global-search">
            <div className={`flex items-center gap-2 border transition-colors ${
                open ? "border-ikb bg-white" : "border-border bg-secondary/40 hover:border-border/70"
            }`}>
                <Search className="w-4 h-4 ml-2.5 text-foreground/60 shrink-0" />
                <input
                    ref={inputRef}
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    placeholder="Search candidates, jobs, clients…"
                    className="flex-1 bg-transparent border-0 outline-none py-2 pr-2 text-sm placeholder:text-foreground/50"
                    data-testid="global-search-input"
                />
                <kbd className="border border-border px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-mono text-foreground/60 mr-2 hidden md:inline">/</kbd>
            </div>

            {open && q.trim() && (
                <div
                    className="absolute left-0 right-0 top-full mt-1 bg-white border border-border shadow-xl z-50 max-h-[70vh] overflow-y-auto"
                    data-testid="global-search-dropdown"
                >
                    {loading && (
                        <div className="p-4 text-xs font-mono text-muted-foreground">Searching…</div>
                    )}
                    {!loading && !hasResults && (
                        <div className="p-6 text-center">
                            <div className="text-sm text-muted-foreground">No matches for <span className="font-mono">"{q}"</span></div>
                        </div>
                    )}
                    {!loading && hasResults && (
                        <div className="divide-y divide-border">
                            {results.candidates?.length > 0 && (
                                <Section label="Candidates" icon={UserIcon}>
                                    {results.candidates.map((c, i) => (
                                        <CandidateRow key={c.id} c={c} active={flat[hi]?.kind === "candidate" && flat[hi]?.item?.id === c.id} onClick={() => go({ kind: "candidate", item: c })} />
                                    ))}
                                </Section>
                            )}
                            {results.jobs?.length > 0 && (
                                <Section label="Jobs" icon={Briefcase}>
                                    {results.jobs.map((j) => (
                                        <RowLink
                                            key={j.id}
                                            title={j.title}
                                            subtitle={[j.status, j.location].filter(Boolean).join(" · ")}
                                            active={flat[hi]?.kind === "job" && flat[hi]?.item?.id === j.id}
                                            onClick={() => go({ kind: "job", item: j })}
                                            testid={`gs-job-${j.id}`}
                                        />
                                    ))}
                                </Section>
                            )}
                            {results.clients?.length > 0 && (
                                <Section label="Clients" icon={Building2}>
                                    {results.clients.map((cl) => (
                                        <RowLink
                                            key={cl.id}
                                            title={cl.name}
                                            subtitle={cl.contact_name || ""}
                                            active={flat[hi]?.kind === "client" && flat[hi]?.item?.id === cl.id}
                                            onClick={() => go({ kind: "client", item: cl })}
                                            testid={`gs-client-${cl.id}`}
                                        />
                                    ))}
                                </Section>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Section({ label, icon: Icon, children }) {
    return (
        <div>
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-mono text-muted-foreground bg-secondary/50 flex items-center gap-1.5">
                <Icon className="w-3 h-3" /> {label}
            </div>
            <div>{children}</div>
        </div>
    );
}

function CandidateRow({ c, active, onClick }) {
    const app = c.primary_application;
    const score = app?.match_score ?? c.match_score;
    return (
        <button
            type="button"
            className={`w-full text-left px-3 py-2.5 hover:bg-secondary/60 border-l-2 ${active ? "border-ikb bg-blue-50/50" : "border-transparent"}`}
            onClick={onClick}
            data-testid={`gs-candidate-${c.id}`}
        >
            <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-ikb/10 text-ikb flex items-center justify-center font-mono text-xs font-bold shrink-0">
                    {(c.name || c.email || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-sm truncate">{c.name || c.email}</div>
                        <UnfitBadge unfit={c.unfit} reason={c.unfit_reason} />
                        {typeof score === "number" && (
                            <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${
                                score >= 80 ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : score >= 50 ? "border-amber-200 bg-amber-50 text-amber-700"
                                : "border-rose-200 bg-rose-50 text-rose-700"
                            }`}>
                                <Sparkles className="w-3 h-3 inline-block -mt-0.5 mr-0.5" />{score}
                            </span>
                        )}
                        {app?.stage && <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border border-border px-1.5 py-0.5">{app.stage}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                        {[c.current_title, c.current_company].filter(Boolean).join(" · ") || c.email}
                    </div>
                    {app?.job_title && (
                        <div className="text-[11px] font-mono text-ikb mt-0.5 truncate">
                            <Briefcase className="w-3 h-3 inline-block -mt-0.5 mr-1" />{app.job_title}
                        </div>
                    )}
                    {app?.ai_summary && (
                        <div className="text-[11px] text-foreground/70 mt-1 line-clamp-2 leading-snug">{app.ai_summary}</div>
                    )}
                </div>
                <ArrowRight className="w-3 h-3 opacity-40 shrink-0 mt-2" />
            </div>
        </button>
    );
}

function RowLink({ title, subtitle, active, onClick, testid }) {
    return (
        <button
            type="button"
            className={`w-full text-left px-3 py-2 hover:bg-secondary/60 border-l-2 flex items-center justify-between gap-3 ${active ? "border-ikb bg-blue-50/50" : "border-transparent"}`}
            onClick={onClick}
            data-testid={testid}
        >
            <div className="min-w-0">
                <div className="font-medium text-sm truncate">{title}</div>
                {subtitle && <div className="text-xs text-muted-foreground truncate">{subtitle}</div>}
            </div>
            <ArrowRight className="w-3 h-3 opacity-40 shrink-0" />
        </button>
    );
}
